import React, { useState } from "react";
import { RoundRobinLeague, LeagueMatch } from "../types/league";
import {
  updateLeagueMatch,
  getCurrentWeekMatches,
  advanceToNextWeek,
} from "../utils/leagueLogic";
import LeagueMatchComponent from "./LeagueMatch";
import {
  Trophy,
  Users,
  Calendar,
  Target,
  Award,
  TrendingUp,
  Clock,
  Star,
  Zap,
  Shield,
  Play,
  ArrowLeft,
  Settings,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
} from "lucide-react";

interface RoundRobinDashboardProps {
  league: RoundRobinLeague;
  setLeague: (league: RoundRobinLeague) => void;
  onReset: () => void;
}

const RoundRobinDashboard: React.FC<RoundRobinDashboardProps> = ({
  league,
  setLeague,
  onReset,
}) => {
  const [selectedMatch, setSelectedMatch] = useState<LeagueMatch | null>(null);
  const [activeView, setActiveView] = useState<
    "standings" | "schedule" | "stats"
  >("standings");
  const [selectedWeek, setSelectedWeek] = useState(league.currentWeek);

  const currentWeekMatches = getCurrentWeekMatches(league);
  const isCurrentWeekComplete = currentWeekMatches.every(
    (match) => match.isCompleted
  );
  const canAdvanceWeek =
    isCurrentWeekComplete && league.currentWeek < league.totalWeeks;

  const handleMatchClick = (match: LeagueMatch | any) => {
    if (!match.isCompleted) {
      setSelectedMatch(match);
    }
  };

  const handleAdvanceWeek = () => {
    if (canAdvanceWeek) {
      const updatedLeague = advanceToNextWeek(league);
      setLeague(updatedLeague);
      setSelectedWeek(updatedLeague.currentWeek);
    }
  };

  const handleEnterScore = (
    matchId: string,
    team1Score: number,
    team2Score: number
  ) => {
    const updatedLeague = updateLeagueMatch(
      league,
      matchId,
      team1Score,
      team2Score
    );
    setLeague(updatedLeague);
    setSelectedMatch(null);
  };

  const getWeekMatches = (weekNumber: number) => {
    const week = league.weeks.find((w) => w.weekNumber === weekNumber);
    return week?.matches || [];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={onReset}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Home
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {league.name}
                </h1>
                <p className="text-sm text-gray-600">
                  Round Robin League • Week {league.currentWeek} of{" "}
                  {league.totalWeeks}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">
                  {league.stats.completedMatches} of {league.stats.totalMatches}{" "}
                  matches completed
                </p>
                <p className="text-xs text-gray-600">
                  {Math.round(
                    (league.stats.completedMatches /
                      league.stats.totalMatches) *
                      100
                  )}
                  % complete
                </p>
              </div>
              {canAdvanceWeek && (
                <button
                  onClick={handleAdvanceWeek}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
                >
                  <ChevronRight className="w-4 h-4 mr-1" />
                  Next Week
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="flex space-x-1 bg-white rounded-lg p-1 shadow-sm mb-8">
          <button
            onClick={() => setActiveView("standings")}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeView === "standings"
                ? "bg-blue-600 text-white"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <Trophy className="w-4 h-4 inline mr-2" />
            Standings
          </button>
          <button
            onClick={() => setActiveView("schedule")}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeView === "schedule"
                ? "bg-blue-600 text-white"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Schedule
          </button>
          <button
            onClick={() => setActiveView("stats")}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeView === "stats"
                ? "bg-blue-600 text-white"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <TrendingUp className="w-4 h-4 inline mr-2" />
            Statistics
          </button>
        </div>

        {/* Standings View */}
        {activeView === "standings" && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  League Standings
                </h2>
                <p className="text-sm text-gray-600">
                  Points: Win = {league.settings.pointsForWin}, Close Loss ={" "}
                  {league.settings.pointsForCloseLoss}, Loss ={" "}
                  {league.settings.pointsForLoss}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rank
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Team
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        W
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        L
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Pts
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Games
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Win %
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {league.standings.map((standing, index) => (
                      <tr
                        key={standing.team.id}
                        className={index < 3 ? "bg-yellow-50" : ""}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {index + 1}
                          {index === 0 && (
                            <Trophy className="w-4 h-4 inline ml-2 text-yellow-600" />
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {standing.team.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {standing.team.player1} & {standing.team.player2}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                          {standing.wins}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                          {standing.losses}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center font-medium text-gray-900">
                          {standing.points}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                          {standing.gamesPlayed}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                          {standing.winPercentage.toFixed(1)}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                          {standing.lastResult === "W" ? (
                            <CheckCircle className="w-4 h-4 text-green-600 mx-auto" />
                          ) : standing.lastResult === "L" ? (
                            <XCircle className="w-4 h-4 text-red-600 mx-auto" />
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Current Week Matches */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Week {league.currentWeek} Matches
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {currentWeekMatches.map((match) => (
                  <LeagueMatchComponent
                    key={match.id}
                    match={match}
                    onClick={handleMatchClick}
                    showWeek={true}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Schedule View */}
        {activeView === "schedule" && (
          <div className="space-y-6">
            {/* Week Selector */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Schedule
                </h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() =>
                      setSelectedWeek(Math.max(1, selectedWeek - 1))
                    }
                    disabled={selectedWeek === 1}
                    className="p-2 rounded-md bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm font-medium">
                    Week {selectedWeek}
                  </span>
                  <button
                    onClick={() =>
                      setSelectedWeek(
                        Math.min(league.totalWeeks, selectedWeek + 1)
                      )
                    }
                    disabled={selectedWeek === league.totalWeeks}
                    className="p-2 rounded-md bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {getWeekMatches(selectedWeek).map((match) => (
                  <LeagueMatchComponent
                    key={match.id}
                    match={match}
                    onClick={handleMatchClick}
                    showWeek={true}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Statistics View */}
        {activeView === "stats" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-2">
                  League Info
                </h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <strong>Teams:</strong> {league.teams.length}
                  </div>
                  <div>
                    <strong>Total Weeks:</strong> {league.totalWeeks}
                  </div>
                  <div>
                    <strong>Current Week:</strong> {league.currentWeek}
                  </div>
                  <div>
                    <strong>Progress:</strong>{" "}
                    {Math.round(
                      (league.stats.completedMatches /
                        league.stats.totalMatches) *
                        100
                    )}
                    %
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-2">Most Wins</h4>
                {league.stats.mostWins ? (
                  <div>
                    <div className="font-semibold text-green-600">
                      {league.stats.mostWins.name}
                    </div>
                    <div className="text-sm text-gray-600">
                      {league.stats.mostWins.player1} &{" "}
                      {league.stats.mostWins.player2}
                    </div>
                    <div className="text-sm text-blue-600 mt-1">
                      {
                        league.standings.find(
                          (s) => s.team.id === league.stats.mostWins?.id
                        )?.wins
                      }
                      W -{" "}
                      {
                        league.standings.find(
                          (s) => s.team.id === league.stats.mostWins?.id
                        )?.losses
                      }
                      L
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500">No matches completed</div>
                )}
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-2">
                  Perfect Games
                </h4>
                <div className="text-3xl font-bold text-purple-600">
                  {league.stats.perfectGames}
                </div>
                <div className="text-sm text-gray-600">
                  Perfect games played
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Score Entry Modal */}
        {selectedMatch && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Enter Match Score
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {selectedMatch.team1.name} Score
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="13"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter score"
                    id="team1Score"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {selectedMatch.team2.name} Score
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="13"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter score"
                    id="team2Score"
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setSelectedMatch(null)}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const team1Score = parseInt(
                      (
                        document.getElementById(
                          "team1Score"
                        ) as HTMLInputElement
                      ).value
                    );
                    const team2Score = parseInt(
                      (
                        document.getElementById(
                          "team2Score"
                        ) as HTMLInputElement
                      ).value
                    );
                    if (!isNaN(team1Score) && !isNaN(team2Score)) {
                      handleEnterScore(
                        selectedMatch.id,
                        team1Score,
                        team2Score
                      );
                    }
                  }}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Submit Score
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default RoundRobinDashboard;
